import { isActivityApiPaginatedResponse } from "@api/activity-api/activity-api-type-guards";
import { sentryCaptureError } from "@utils/sentry-utils";

/** Number of activity items per page that we will fetch from the ActivityAPI */
const ITEMS_PER_PAGE = 30;
/**
 * An api client to handle request to activity tracking api
 * https://faro01.atlassian.net/wiki/spaces/HB/pages/3873013913/Activity+Tracking+API+aka+Analytics+Backend
 *
 * OpenApi documentation:
 * https://activity.api.dev.holobuilder.eu/swagger/index.html#tag/Activity/paths/~1v1~1activities/post
 */
export class ActivityApiClient {
  #companyId;
  #projectId;
  #tokenProvider;
  #activityApiBaseUrl;
  #clientId;

  /** Create a new activity api client */
  constructor(params) {
    this.#companyId = params.companyId;
    this.#projectId = params.projectId;
    this.#activityApiBaseUrl = params.activityApiBaseUrl;
    this.#tokenProvider = params.tokenProvider;
    this.#clientId = params.clientId;
  }

  /**
   * @returns Paginated list of credit transactions
   */
  async fetchCreditTransaction({
    companyId,
    projectId,
    after,
    pageSize = ITEMS_PER_PAGE
  } = {}) {
    const searchParams = new URLSearchParams();
    searchParams.append("tags", "tokens");

    // Adding companyId if already provided in constructor
    const companyIdToSearch = this.#companyId ?? companyId;
    if (companyIdToSearch) {
      searchParams.append("companyId", companyIdToSearch);
    }

    // Adding projectId if already provided in constructor
    const projectIdToSearch = this.#projectId ?? projectId;
    if (projectIdToSearch) {
      searchParams.append("projectId", projectIdToSearch);
    }

    // Adding the after cursor if provided to fetch the next page
    if (after) {
      searchParams.append("after", after);
    }
    searchParams.append("pageSize", pageSize.toString());
    const url = new URL(`${this.#activityApiBaseUrl}/v1/activities?${searchParams.toString()}`);
    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${await this.#tokenProvider()}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ...(this.#clientId && {
          "X-Holobuilder-Component": this.#clientId
        })
      }
    });
    if (!res.ok) {
      throw new Error(`Transactions request failed: ${res.status} ${res.statusText}`);
    }
    const response = await res.json();

    // Validate the response structure to check if it matches the expected ActivityApiPaginatedResponse type
    if (!isActivityApiPaginatedResponse(response)) {
      const error = new Error("ActivityAPI returned invalid response", {
        cause: response
      });

      // Silently log the error to Sentry for further investigation
      sentryCaptureError({
        error,
        title: "ActivityAPI returned invalid response"
      });
    }
    return response;
  }
}